import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';
import { Alert } from 'react-native';

type AuthContextType = {
  session: Session | null;
  isLoading: boolean;
  signIn: (provider: 'google' | 'email', credentials?: { email: string; password: string }) => Promise<void>;
  signUp: (credentials: { email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
  user: any;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (provider: 'google' | 'email', credentials?: { email: string; password: string }) => {
    try {
      // If the user chooses to continue as guest, bypass Supabase auth and create a dummy session.
      if (provider === 'email' && credentials?.email === "guest@example.com" && credentials?.password === "guestpassword123") {
        const guestSession = {
          user: {
            id: "guest",
            email: credentials.email,
          },
          // Optionally add any other fields needed by your app
        } as unknown as Session;
        setSession(guestSession);
        return;
      }

      if (provider === 'google') {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
          },
        });
        if (error) throw error;
      } else if (credentials) {
        const { error } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });
        if (error) throw error;
      }
    } catch (error: any) {
      Alert.alert('Error', error.message);
      throw error;
    }
  };

  const signUp = async (credentials: { email: string; password: string }) => {
    try {
      const { error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { email_confirmed: true }
        }
      });
      if (error) throw error;
      Alert.alert('Success', 'Account created! You can now sign in.');
    } catch (error: any) {
      Alert.alert('Error', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setSession(null);
    } catch (error: any) {
      Alert.alert('Error', error.message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ session, isLoading, signIn, signUp, signOut, user: session?.user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 